import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * ModalSlideout - A reusable, accessible, and animated slide-out modal component.
 *
 * Features:
 * - Automatically finds and connects to its trigger element via `aria-controls`.
 * - Closes when clicking the overlay or pressing "Escape" (unless disabled).
 * - Supports multiple animation directions.
 * - Moves itself into `<body>` to prevent z-index issues.
 * - Ensures only one modal is open at a time.
 *
 * Accessibility:
 * - Uses `aria-controls` to link the trigger button.
 * - Updates `aria-expanded` on the trigger element when opened/closed.
 * - Adds `aria-modal="true"` and `role="dialog"` for proper accessibility.
 *
 * Notes:
 * - If multiple modals exist, opening a new one will close any currently open modals.
 * - The modal is automatically moved to `<body>` for correct stacking.
 */

class ModalSlideout extends HTMLElement {
  /** @type {HTMLDivElement|null} */
  static overlay = null;

  /** @type {boolean} */
  isOpen = false;

  /** @type {HTMLElement|null} */
  triggerElement = null;

  /** @type {HTMLElement[]|undefined} */
  triggerElements;

  /** @type {boolean|undefined} */
  closeOnEsc;

  /** @type {string|undefined} */
  animation;

  /** @type {{ $modal: Element|null, $closeBtn: NodeListOf<HTMLElement>|null }|undefined} */
  viewModel;

  /** @type {((e: MouseEvent) => void) | null | undefined} */
  _outsideClickHandler;

  constructor() {
    super();
    this.isOpen = false;
    this.triggerElement = null;
    this.bindEvents();
  }

  /**
   * Validates if the modal contains the required `.slideout` structure
   * @returns {boolean} - Returns true if valid, false if invalid
   */
  validateStructure() {
    if (!this.querySelector('.slideout')) {
      console.warn(
        `Skipping initialization: modal-slideout (id="${this.id}") is missing a .slideout wrapper.`
      );
      this.remove();
      return false;
    }
    return true;
  }

  /**
   * Moves the modal to the <body> to avoid z-index issues
   */
  moveToBody() {
    // Don't move bottom sheet modal on desktop
    if (
      !window.utilities.isMobileBreakpoint() &&
      this.animation === 'slide-bottom'
    )
      return;

    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
  }

  /**
   * Creates a shared overlay if it doesn't exist
   */
  initializeOverlay() {
    if (!ModalSlideout.overlay) {
      ModalSlideout.overlay = document.createElement('div');
      ModalSlideout.overlay.classList.add('slideout-modal-overlay');
      ModalSlideout.overlay.style.display = 'none';
      document.body.appendChild(ModalSlideout.overlay);

      ModalSlideout.overlay.addEventListener('click', () => {
        /** @type {NodeListOf<ModalSlideout>} */
        const openModals = document.querySelectorAll('modal-slideout[open]');
        openModals.forEach((modal) => modal.close());
      });
    }
  }

  /**
   * Defines all the elements inside the component
   * @returns {{ $modal: Element|null, $closeBtn: NodeListOf<HTMLElement>|null }}
   */
  getViewModel() {
    return {
      $modal: this.querySelector('.slideout'),
      $closeBtn: this.querySelectorAll('.close-btn'),
    };
  }

  initializeAttributes() {
    this.closeOnEsc = this.getAttribute('close-on-esc') !== 'false';
    this.animation = this.getAttribute('animation') || 'slide-right';
  }

  bindEvents() {
    if (!this.validateStructure()) return;

    this.removeAttribute('hidden');
    this.initializeAttributes();
    this.moveToBody();
    this.initializeOverlay();

    this.viewModel = this.getViewModel();

    if (this.hasAttribute('open')) {
      this.open();
    }

    if (this.viewModel.$closeBtn) {
      this.viewModel.$closeBtn.forEach((cb) => {
        cb.addEventListener('click', () => this.close());
      });
    }

    if (this.closeOnEsc) {
      document.addEventListener('keydown', (e) => this.handleEscape(e));
    }

    this.findTriggerElements();
  }

  /** @param {KeyboardEvent} event */
  handleEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  findTriggerElements() {
    /** @type {NodeListOf<HTMLElement>} */
    const triggers = document.querySelectorAll(`[aria-controls="${this.id}"]`);
    if (!triggers.length) return;

    /** @type {HTMLElement[]} */
    this.triggerElements = Array.from(triggers);

    this.triggerElements.forEach((trigger) => {
      trigger.addEventListener('click', () => this.open(trigger));
    });
  }

  /** @param {HTMLElement=} trigger */
  open(trigger) {
    this.triggerElement = trigger ?? null;

    if (this.triggerElement) {
      this.triggerElement.setAttribute('aria-expanded', 'true');
    }

    /** @type {(e: MouseEvent) => void} */
    this._outsideClickHandler = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (!this.contains(t)) this.close();
    };

    document.addEventListener('mousedown', this._outsideClickHandler);

    this.closeAll();
    this.setAttribute('open', '');
    this.isOpen = true;
    if (ModalSlideout.overlay) {
      ModalSlideout.overlay.style.display = 'block';
    }

    trapFocus(this);
  }

  close() {
    if (this.triggerElement) {
      this.triggerElement.setAttribute('aria-expanded', 'false');
      this.triggerElement.classList.remove('is-active');
    }

    if (this._outsideClickHandler) {
      document.removeEventListener('mousedown', this._outsideClickHandler);
      this._outsideClickHandler = null;
    }

    this.removeAttribute('open');
    this.isOpen = false;

    if (!document.querySelector('modal-slideout[open]')) {
      if (ModalSlideout.overlay) ModalSlideout.overlay.style.display = 'none';
    }

    removeTrapFocus();
  }

  disableAnimationTemporary() {
    this.classList.add('no-animation');

    setTimeout(() => {
      this.classList.remove('no-animation');
    }, 400);
  }

  closeAll() {
    const list = document.querySelectorAll('modal-slideout[open]');
    if (!list.length) return;

    if (window.utilities.isMobileBreakpoint()) this.disableAnimationTemporary();

    list.forEach((el) => {
      if (el instanceof ModalSlideout) el.close();
    });
  }
}

customElements.define('modal-slideout', ModalSlideout);
